import React, { useEffect, useRef } from 'react'
import { Partida } from '../services/partidaService'
import { atletasDoLado, atletaEstaNoLado, ladoVencedor } from '../utils/atletasDoLado'

export interface BasicAtletaInfo {
  id: string
  nome: string
  username?: string | null
  imagemUrl?: string | null
}

export interface AtletaMatchesModalProps {
  open: boolean
  atleta: BasicAtletaInfo
  parceiro?: BasicAtletaInfo | null
  partidas: Partida[]
  onClose: () => void
  torneioId?: string
  categoriaId?: string
  categoriaNome?: string
  torneioAvatarUrl?: string | null
  onNavigateToCategory?: () => void
}

const AtletaMatchesModal: React.FC<AtletaMatchesModalProps> = ({
  open,
  atleta,
  parceiro,
  partidas,
  onClose,
  torneioId,
  categoriaId,
  categoriaNome,
  torneioAvatarUrl,
  onNavigateToCategory,
}) => {
  const modalRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    if (open) {
      document.addEventListener('keydown', handleKeyDown)
      document.body.classList.add('modal-open')
      modalRef.current?.focus()
    }
    return () => {
      document.removeEventListener('keydown', handleKeyDown)
      document.body.classList.remove('modal-open')
    }
  }, [open, onClose])

  if (!open) return null

  const handleBackdrop = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) onClose()
  }

  const formatDate = (d: string) =>
    new Date(d).toLocaleString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    }
  )

  const getPartidaStatus = (match: Partida): 'vitoria' | 'derrota' | 'pendente' | 'empate' => {
    // Verificar se a partida está finalizada
    if (match.status !== 'FINALIZADA') {
      return 'pendente'
    }

    // Identificar o lado do atleta (equipe ou participante avulso — rodízio)
    const isA = atletaEstaNoLado(match, 'A', atleta.id)
    const isB = atletaEstaNoLado(match, 'B', atleta.id)

    // Se não estiver em nenhum lado, considerar pendente
    if (!isA && !isB) {
      return 'pendente'
    }

    // Usar o lado vencedor ("A"/"B") para determinar o resultado — cobre rodízio
    // (equipeA/B nulos) e legado (a fonte única normaliza ambos).
    const ladoV = ladoVencedor(match)
    if (ladoV) {
      return ladoV === (isA ? 'A' : 'B') ? 'vitoria' : 'derrota'
    }

    // Se não houver equipeVencedora, verificar se há placar para determinar empate
    const resA = match.placar?.resultadoFinalA
    const resB = match.placar?.resultadoFinalB
    if (resA != null && resB != null) {
      if (resA === resB) {
        return 'empate'
      }
    }

    return 'pendente'
  }
    

  const renderResultado = (match: Partida) => {
    const resA = match.placar?.resultadoFinalA
    const resB = match.placar?.resultadoFinalB
    if (resA == null || resB == null) return ''
    return `${resA} / ${resB}`
  }


  const getAdversarios = (match: Partida) => {
    // Lado do atleta (equipe ou participante avulso — rodízio); adversário é o outro lado.
    const isA = atletaEstaNoLado(match, 'A', atleta.id)
    const ladoAtleta = atletasDoLado(match, 'A').length > 0 || atletasDoLado(match, 'B').length > 0
    if (!ladoAtleta) return []
    const ladoAdversario: 'A' | 'B' = isA ? 'B' : 'A'
    return atletasDoLado(match, ladoAdversario).map(a => ({
      nome: a.nome,
      imagemUrl: a.imagemUrl,
    }))
  }

  return (
    <>
      <div className="modal-backdrop fade show" onClick={onClose} />
      <div
        className="modal fade show d-block"
        role="dialog"
        tabIndex={-1}
        ref={modalRef}
        onMouseDown={handleBackdrop}
        style={{ overflow: 'auto' }}
      >
        <div className="modal-dialog modal-lg modal-dialog-centered" role="document" onMouseDown={e => e.stopPropagation()}>
          <div className="modal-content">
            <div className="modal-header">
              <div className="d-flex align-items-center gap-3 w-100">
                {torneioAvatarUrl && (
                  <img
                    src={torneioAvatarUrl}
                    alt="Avatar do torneio"
                    className="rounded-circle"
                    style={{ width: 64, height: 64, objectFit: 'cover' }}
                  />
                )}
                <div className="flex-grow-1">
                  <h2 className="modal-title fw-600 mb-0">
                    {categoriaNome ? `${categoriaNome} - Trilha do Torneio` : 'Trilha do Torneio'}
                  </h2>
                </div>
                {onNavigateToCategory && torneioId && categoriaId && (
                  <button
                    type="button"
                    className="btn btn-primary btn-sm"
                    onClick={onNavigateToCategory}
                  >
                    Ver todas as partidas
                  </button>
                )}
                <button type="button" className="btn-close" aria-label="Fechar" onClick={onClose} />
              </div>
            </div>
            <div className="modal-body">
              <div className='row'> {/* row equipe info */}
                <div className="col d-flex align-items-center gap-3 mb-3"> {/* coluna atleta */}
                  <div className='row align-items-center '>
                    <div className='col d-flex justify-content-end'>
                      <img
                        src={atleta.imagemUrl || '/assets/images/avatar.jpg'}
                        alt={atleta.nome}
                        className="rounded-circle"
                        style={{ width: 96, height: 96, objectFit: 'cover' }}
                      />
                    </div>
                    <div className='col d-flex justify-content-start'>
                      <div className='row'>
                        <div className='fw-600'>
                          {atleta.nome}
                        </div>
                        <div className="text-primary">
                          @{atleta.username}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                {parceiro && (
                  <div className="col d-flex align-items-center gap-3 mb-3"> {/* coluna parceiro */}
                    <div className='row align-items-center'>
                    
                      
                      <div className='col d-flex justify-content-end'>
                        <img
                          src={parceiro.imagemUrl || '/assets/images/avatar.jpg'}
                          alt={parceiro.nome}
                          className="rounded-circle"
                          style={{ width: 96, height: 96, objectFit: 'cover' }}
                        />
                      </div>
                      <div className='col d-flex justify-content-start'>
                        <div className='row'>
                          <div className='fw-600'>
                            {parceiro.nome}
                          </div>
                          <div className="text-primary">
                            @{parceiro.username}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
              
              <h3 className="modal-title w-100 fw-600 mt-2">Jogos realizados</h3>
              {partidas.map(p => {
                const status = getPartidaStatus(p)
                return (
                  <React.Fragment key={p.id}>
                    <hr className='m-1'/>
                    <div className="row mb-3 pb-2 align-items-center flex-nowrap" style={{ overflowX: 'auto' }}>
                      <div className="col-auto" style={{ minWidth: '120px', flexShrink: 0 }}>
                        <div className="">{p.faseCategoriaTorneio?.nome || ''}</div>
                        <div className="text-muted small">Jogo #{p.faseCategoriaTorneio?.ordem || ''}</div>
                      </div>
                      <div className='col-auto' style={{ minWidth: '120px', flexShrink: 0 }}>
                        <div className="">
                          {status === 'vitoria' && <span className="text-success">Venceu</span>}
                          {status === 'derrota' && <span className="text-danger">Perdeu</span>}
                          {status === 'pendente' && <span className="text-muted">Pendente</span>}
                          {status === 'empate' && <span className="text-warning">Empate</span>}
                        </div>
                        <div className="text-muted small">{formatDate(p.data)}</div>
                      </div>
                      <div className="col-auto mt-1" style={{ flexShrink: 0 }}>
                        <div className="d-flex gap-1 justify-content-start">
                          {getAdversarios(p).map((adv, i) => (
                            <img
                              key={i}
                              src={adv.imagemUrl || '/assets/images/avatar.jpg'}
                              alt={adv.nome}
                              className="rounded-circle"
                              style={{ width: 56, height: 56, objectFit: 'cover' }}
                            />
                          ))}
                        </div>
                      </div>
                      <div className="col-auto d-flex flex-column align-items-start" style={{ minWidth: '150px', flexShrink: 0 }}>
                        {getAdversarios(p).map((adv, i) => (
                          <div key={i} className="text-start text-truncate" style={{ maxWidth: '100%' }}>
                            {adv.nome}
                          </div>
                        ))}
                      </div>
                      <div className="col-auto d-flex flex-column align-items-end" style={{ flexShrink: 0 }}>
                        <div className="text-end fw-bold fs-5" style={{ whiteSpace: 'nowrap' }}>
                          {renderResultado(p) || 'Aguardando resultado'}
                        </div>
                      </div>
                    </div>
                  </React.Fragment>
                )
              })}
            </div>
          </div>
        </div>
      </div>
    </>
  )
}

export default AtletaMatchesModal
